/**
 * BPS Theme Configuration
 *
 * Main theme export that integrates all theme modules:
 * - Colors and design tokens
 * - Light/dark mode palettes
 * - Typography system
 * - Component overrides
 * - TypeScript augmentations
 *
 * Features:
 * - CSS variables for runtime theme switching
 * - Class-based color scheme selector for manual mode control
 * - Consistent spacing and shape system
 * - Accessibility-first design
 *
 * Best Practices:
 * - Import this as a static theme (no hooks needed)
 * - Use with InitColorSchemeScript for SSR
 * - Enable disableTransitionOnChange for better UX
 *
 * @example
 * ```tsx
 * import { InitColorSchemeScript, ThemeProvider } from '@mui/material';
 * import bpsTheme from './theme/theme';
 *
 * <InitColorSchemeScript attribute="class" defaultMode="system" />
 * <ThemeProvider theme={bpsTheme} defaultMode="system" disableTransitionOnChange>
 *   <App />
 * </ThemeProvider>
 * ```
 *
 * @see https://mui.com/material-ui/customization/theming/
 * @see https://mui.com/material-ui/customization/dark-mode/
 */

import { createTheme } from '@mui/material/styles';
import { lightPalette, darkPalette } from './theme-palette';
import { typography, variantMapping } from './theme-typography';
import { components } from './theme-components';
import { themeTokens } from './theme-tokens';

// Type augmentations are in theme-augment.d.ts and will be picked up by TypeScript automatically

/**
 * BPS Theme
 *
 * Configured with:
 * - CSS variables enabled with class-based selector
 * - Light and dark color schemes
 * - Custom typography with brand variants
 * - Component style overrides
 * - Consistent spacing (8px base) and shape (12px border radius)
 */
export const bpsTheme = createTheme({
  // Enable CSS variables with class-based color scheme switching
  // This allows manual theme switching via setMode() and proper SSR support
  cssVariables: {
    colorSchemeSelector: 'class',
  },

  // Color schemes for light and dark modes
  colorSchemes: {
    light: {
      palette: lightPalette,
    },
    dark: {
      palette: darkPalette,
    },
  },

  // Typography configuration
  typography: {
    ...typography,
  },

  // Shape system (border radius)
  shape: {
    borderRadius: themeTokens.borderRadius.medium,
  },

  // Spacing system (8px base unit)
  spacing: 8,

  // Component style overrides
  components,
});

// Apply custom typography variant mapping
// This maps custom variants (headerText, etc.) to semantic HTML elements
if (bpsTheme.typography) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (bpsTheme.typography as any).variantMapping = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...(bpsTheme.typography as any).variantMapping,
    ...variantMapping,
  };
}

export default bpsTheme;
